import React, { useMemo } from "react";
import { SelectProps } from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import { MomentChangeHandler } from "@core/interfaces/moment-change-handler";
import { motionSensorMode } from "@core/interfaces/motion-sensor-mode";
import { motionSensorAmpMode } from "@core/interfaces/motion-sensor-amp-mode";
import { MomentErrorHandler } from "@core/interfaces/moment-error-handler";
import { lang } from "@lang/interfaces/lang";
import SelectComponent from "@ui/components/Select/Select.component";
import TimeInputComponent from "@ui/components/Time-input/Time-input.component";
import useDictionary from "@ui/hooks/useDictionary";
import SwitchComponent from "./Switch.component";
import { IDateInput } from "./useDeviceSettings";
import { DeviceTypes } from "@core/enums/device-types";

interface IProps {
    lang: lang;
    motionSensorMode: number;
    motionSensorModes: motionSensorMode[];
    periodStart: IDateInput;
    periodEnd: IDateInput;
    accelerometerAmp: number | null;
    motionSensorAmpModes: motionSensorAmpMode[];
    isNotIgnore: boolean;
    ignoreTime: IDateInput;
    preliminaryAlarmSend: boolean;
    preliminaryAlarmSendSupported: boolean;
    deviceType: string;
    sensorMode: number;

    onMotionSensorModeChange: SelectProps["onChange"];
    onSensorModeChange: SelectProps["onChange"];
    onPeriodStartChange: MomentChangeHandler;
    onPeriodEndChange: MomentChangeHandler;
    onPeriodStartError: MomentErrorHandler;
    onPeriodEndError: MomentErrorHandler;
    onAccelerometerAmpChange: SelectProps["onChange"];
    onNotIgnoreChange: () => void;
    onPreliminaryAlarmSendChange: () => void;
    onIgnoreTimeChange: MomentChangeHandler;
    onIgnoreTimeError: MomentErrorHandler;
    onInputClick: () => void;
}

const Component: React.FC<IProps> = ({
    lang,
    motionSensorMode,
    motionSensorModes,
    periodStart,
    periodEnd,
    accelerometerAmp,
    motionSensorAmpModes,
    isNotIgnore,
    ignoreTime,
    preliminaryAlarmSend,
    preliminaryAlarmSendSupported,
    deviceType,
    sensorMode,
    onMotionSensorModeChange,
    onPeriodStartChange,
    onPeriodEndChange,
    onPeriodStartError,
    onPeriodEndError,
    onAccelerometerAmpChange,
    onNotIgnoreChange,
    onPreliminaryAlarmSendChange,
    onIgnoreTimeChange,
    onIgnoreTimeError,
    onInputClick,
    onSensorModeChange
}: IProps) => {
    const getWord = useDictionary();
    const ampLen = motionSensorAmpModes.length;
    const ampList = useMemo(
        () =>
            motionSensorAmpModes.map((label, i) => {
                const value = (ampLen - i) * 10;
                return { value, label };
            }),
        [motionSensorAmpModes, ampLen]
    );
    const modeList = useMemo(
        () => motionSensorModes.map((label, value) => ({ label, value })),
        [motionSensorModes]
    );

    const period = useMemo(() => {
        if (motionSensorMode === 2) {
            return (
                <>
                    <TimeInputComponent
                        label="periodStart"
                        tooltip="periodStartTooltip"
                        helperText=""
                        lang={lang}
                        value={periodStart.value}
                        onChange={onPeriodStartChange}
                        onError={onPeriodStartError}
                        onInputClick={onInputClick}
                    />
                    <TimeInputComponent
                        label="periodEnd"
                        tooltip="periodEndTooltip"
                        helperText=""
                        lang={lang}
                        value={periodEnd.value}
                        onChange={onPeriodEndChange}
                        onError={onPeriodEndError}
                        onInputClick={onInputClick}
                    />
                </>
            );
        } else {
            return <></>;
        }
    }, [
        motionSensorMode,
        lang,
        periodStart.value,
        periodEnd.value,
        onPeriodStartChange,
        onPeriodStartError,
        onPeriodEndChange,
        onPeriodEndError,
        onInputClick,
    ]);

    const motionSensorModesTooltip = useMemo(
        () =>
            new Array(3)
                .fill(0)
                .map(
                    (v, i) =>
                        `${i + 1}. ${getWord(`motionSensorModesTooltip_${i}`)}.`
                ),
        [lang]
    );

    /**
     * Отображать ли переключатель режима работы акселерометра ( выбор Движения / Наклона )
     */
    const showMotionAngleSelection = deviceType.startsWith("MX500") || deviceType.startsWith("MX510") || deviceType.startsWith("MX600");

    return (
        <FormControl className="device-settings__form" component="form">
            {showMotionAngleSelection && (
                <SelectComponent
                    label="motionOrAngle"
                    name="motion-or-angle"
                    lang={lang}
                    description={"sensorModeTooltip"}
                    value={sensorMode}
                    values={[
                        {
                            label: "motionMode",
                            value: 0,
                        },
                        {
                            label: "angleMode",
                            value: 1,
                        },
                    ]}
                    onChange={onSensorModeChange}
                    onClick={onInputClick}
                    onFocus={onInputClick}
                />
            )}
            <SelectComponent
                label="operationMode"
                name="operation-mode"
                lang={lang}
                description={motionSensorModesTooltip}
                value={motionSensorMode}
                values={modeList}
                onChange={onMotionSensorModeChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            {period}
            <SelectComponent
                label="accelerometerAmp"
                name="accelerometerAmp"
                description="accelerometerAmpTooltip"
                lang={lang}
                value={accelerometerAmp}
                values={ampList}
                disabled={motionSensorMode === 0}
                onChange={onAccelerometerAmpChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            <TimeInputComponent
                label="sensorIgnoreTime"
                tooltip="ignoreTimeTooltip"
                helperText="ignoreHelperText"
                lang={lang}
                disabled={isNotIgnore || motionSensorMode === 0}
                maxTime={{ hour: 12, minute: 0 }}
                value={ignoreTime.value}
                onChange={onIgnoreTimeChange}
                onError={onIgnoreTimeError}
                onInputClick={onInputClick}
            />
            <SwitchComponent
                alarmState={isNotIgnore}
                name={"notToIgnore"}
                disabled={motionSensorMode === 0}
                tooltip={getWord("notToIgnoreTooltip")}
                onAlarmStateChange={onNotIgnoreChange}
            />
            <SwitchComponent
                alarmState={preliminaryAlarmSend}
                name="preliminaryAlarmSend"
                disabled={!preliminaryAlarmSendSupported}
                tooltip={getWord("preliminaryAlarmSendTooltip")}
                onAlarmStateChange={onPreliminaryAlarmSendChange}
            />
        </FormControl>
    );
};

const MotionSensorSettingsComponent = React.memo(Component);

export default MotionSensorSettingsComponent;
